/**
 * BrandSelector — header control to pick the active brand theme (Faz-5 PR-5, #441).
 *
 * Lists the brands the user owns that are `themed_by` an active design system;
 * selecting one applies the brand's tokens via the cascade (store.selectBrand),
 * "Varsayılan" clears back to the PR-3 default chrome.
 *
 * GRACEFUL 0 / 1 / N (brief requirement — never crash, never show an empty menu):
 *   - 0 themeable brands → render NOTHING (`return null`). A user with no brand
 *     (the common case) sees no extra header control.
 *   - 1+ themeable brands → render the dropdown (the "Varsayılan" reset is always
 *     offered alongside, so even a single brand is meaningfully toggleable).
 *
 * Visual: mirrors the Header's existing dropdown (button + absolute panel +
 * outside-click close). Dark-mode variants are explicit on every class. The
 * control is part of the fixed `h-16` header — no own height/scroll concerns.
 */
import { Palette, Check, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useActiveBrandStore } from '../../stores/activeBrand.store.js';
import { cn } from '../../lib/utils.js';

export function BrandSelector() {
  const owned = useActiveBrandStore((s) => s.owned);
  const selectedBrandId = useActiveBrandStore((s) => s.selectedBrandId);
  const loading = useActiveBrandStore((s) => s.loading);
  const selectBrand = useActiveBrandStore((s) => s.selectBrand);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click (same pattern as the visual-adjustments panel).
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Only brands themed by an active design system actually change the chrome.
  const themeable = owned.filter((b) => b.themed);

  // GRACEFUL 0: no themeable brand → no control at all.
  if (themeable.length === 0) return null;

  const selected = themeable.find((b) => b.brandId === selectedBrandId) ?? null;

  const choose = (brandId: number | null) => {
    setOpen(false);
    if (brandId === selectedBrandId) return; // no-op re-select
    void selectBrand(brandId);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
        title="Marka teması"
        className={cn(
          'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
          selected
            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
            : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800',
        )}
      >
        <Palette className="h-4 w-4" />
        <span className="max-w-[8rem] truncate">{selected ? selected.name : 'Tema'}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
          {/* Default / no-brand reset */}
          <button
            type="button"
            onClick={() => choose(null)}
            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <span>Varsayılan</span>
            {selectedBrandId === null && <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
          </button>

          <div className="my-1 h-px bg-gray-100 dark:bg-gray-700" />

          {themeable.map((brand) => (
            <button
              key={brand.brandId}
              type="button"
              onClick={() => choose(brand.brandId)}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <span className="truncate">{brand.name}</span>
              {brand.brandId === selectedBrandId && (
                <Check className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
